import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Container, Spinner } from 'react-bootstrap';

import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ACTING_AS_STORAGE_KEY,
  readActingAsEntity,
  writeActingAsEntity,
  type ActingAsEntity,
} from '../../../../v2/js/utils/actingAsEntity';
import { localStore } from '../../../../v2/js/utils/storage';
import { ApiError, AuthService, HealthcareEntitiesService, UserTypeEnum } from '../../../api';
import { setSessionHandler } from '../../../api/core/request';
import {
  isOptionalAuthRoutePath,
  isSessionEstablishmentExemptPath,
} from '../../../utils/anonymousRoutes';
import { clearBrowserCookies, getCookieValue } from '../../../utils/cookies';
import { SessionContext } from '../../contexts/SessionContext';
import { useCurrentUser, useCurrentUserNotifications } from '../../hooks/auth';
import { handleApiError } from '../../utils/errorhandling';
import { identifyUser } from '../../utils/logrocket';

interface Props {
  children?: ReactNode;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function Session({ children }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [actingAsEntity, _setActingAsEntity] = useState<ActingAsEntity | null>(() =>
    readActingAsEntity()
  );

  const setActingAsEntity = useCallback(
    (entity: ActingAsEntity | null) => {
      writeActingAsEntity(entity);
      _setActingAsEntity(entity);
      queryClient.invalidateQueries();
    },
    [queryClient]
  );

  // Cross-tab sync: pick up changes made in other tabs.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== ACTING_AS_STORAGE_KEY) return;
      _setActingAsEntity(readActingAsEntity());
      queryClient.invalidateQueries();
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [queryClient]);

  // Use a ref for location so the session handler always reads the current
  // location at the time it fires, without re-registering on every navigation.
  // Re-registering on location change was causing a loop: navigate to /login
  // → location changes → handler re-registered → could fire again.
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // Set up session expiration handler — register once, reads location via ref
  useEffect(() => {
    setSessionHandler({
      handleSessionExpired: (isAuthError: boolean = false) => {
        // Clear all session data
        queryClient.cancelQueries();
        queryClient.clear();
        clearBrowserCookies();
        writeActingAsEntity(null);
        _setActingAsEntity(null);
        setSessionExists(false);
        setInitialCheckComplete(true);
        Sentry.setUser(null);
        console.log('Session expired, redirecting to login page');

        const currentLocation = locationRef.current;

        // Don't create nested redirects if we're already on login page
        if (currentLocation.pathname.startsWith('/login')) {
          console.log('Already on login page, not redirecting');
          return;
        }

        // Build the login URL with next parameter and session expired flag
        const currentPath = currentLocation.pathname + currentLocation.search;
        const nextParam = encodeURIComponent(currentPath);
        const sessionExpiredParam = isAuthError ? '&session_expired=true' : '';
        const loginUrl = `/login?next=${nextParam}${sessionExpiredParam}`;

        // Hard reload (not react-router navigate) so Django serves a fresh
        // index.html with @ensure_csrf_cookie, guaranteeing a valid csrftoken
        // cookie before the next login POST. Without this, the browser keeps
        // a stale (HttpOnly) sessionid while the JS-visible csrftoken is gone,
        // and DRF's SessionAuthentication rejects login with "CSRF cookie not set."
        window.location.assign(loginUrl);
      },
    });
    // Only re-register when navigate or queryClient change (stable references),
    // NOT on location change — that was the root cause of the loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, queryClient]);

  // Don't assume session exists if we're on a social login callback route
  const isOAuthCallback =
    location.pathname.includes('/social-login/') &&
    (location.search.includes('code=') || location.search.includes('state='));

  // Pages that should not attempt session establishment. Pulled from a
  // shared helper so the global axios interceptor can mirror the same
  // intent and avoid bouncing 403s from these routes to /login.
  const isSessionEstablishmentExempt = isSessionEstablishmentExemptPath(location.pathname);

  // Pages that allow optional authentication (can be accessed anonymously, but will fetch user if logged in)
  const isOptionalAuthRoute = isOptionalAuthRoutePath(location.pathname);

  const isRootRoute: boolean = location.pathname === '/';

  // Don't check for session on exempt routes (but allow optional auth routes)
  const shouldSkipSessionCheck = isOAuthCallback || isSessionEstablishmentExempt;

  // Check if there's actually a session cookie before trying to fetch user data
  const hasSessionIndicator = !!getCookieValue('csrftoken');

  // For login page, we want to check for existing session if cookie exists
  const isLoginPage = location.pathname.startsWith('/login');

  // Initialize session state
  // For optional auth routes, set sessionExists if we have a cookie (but don't require it)
  const [sessionExists, setSessionExists] = useState(
    isOptionalAuthRoute
      ? hasSessionIndicator
      : !shouldSkipSessionCheck && (hasSessionIndicator || (!isLoginPage && !isRootRoute))
  );

  // Initial check is complete for exempt routes or when no session cookie exists on login page
  // For optional auth routes, we're done if no cookie exists, otherwise wait for user fetch
  const [initialCheckComplete, setInitialCheckComplete] = useState(
    isOptionalAuthRoute
      ? !hasSessionIndicator
      : shouldSkipSessionCheck || ((isLoginPage || isRootRoute) && !hasSessionIndicator)
  );

  // Determine when to attempt user fetch
  // For optional auth routes, attempt fetch if we have a session cookie
  // For other routes, use the existing logic
  const shouldAttemptUserFetch = isOptionalAuthRoute
    ? hasSessionIndicator
    : sessionExists && !shouldSkipSessionCheck && (hasSessionIndicator || !isLoginPage);

  const currentUserData = useCurrentUser({
    enabled: shouldAttemptUserFetch,
    onSuccess: (userData) => {
      // Set Sentry user on successful auth
      if (userData) {
        Sentry.setUser({
          id: userData.id,
          email: userData.email,
          user_type: userData.user_type,
        });
        identifyUser(userData.id, {
          name: userData.first_name + ' ' + userData.last_name,
          email: userData.email,
        });
        // techdebt: seems to be used for resend verification email... not a good solution
        localStore.set('authLastSigninEmail', userData.email);
      }
      setInitialCheckComplete(true);
    },
    onError: (error) => {
      // For optional auth routes, don't show error if user fetch fails (user might not be logged in)
      if (!isOptionalAuthRoute) {
        handleApiError(error);
      }
      setInitialCheckComplete(true);
    },
  });

  const currentUserNotifications = useCurrentUserNotifications({
    enabled:
      (sessionExists || isOptionalAuthRoute) &&
      !currentUserData.isLoading &&
      !currentUserData.errors &&
      !shouldSkipSessionCheck,
  });

  const mutationLogout = useMutation({
    mutationFn: async () => {
      await AuthService.authLogoutCreate();
    },
    onSuccess: () => {
      queryClient.clear();
      clearBrowserCookies();
      writeActingAsEntity(null);
      _setActingAsEntity(null);
      setSessionExists(false);
      currentUserData.remove();
      navigate('/');
    },
    onError: (errors: ApiError) => {
      toast.error(errors.body?.detail || "Couldn't logout");
      // Even if logout fails on server, clear client state
      queryClient.clear();
      clearBrowserCookies();
      writeActingAsEntity(null);
      _setActingAsEntity(null);
      setSessionExists(false);
      currentUserData.remove();
      navigate('/');
    },
  });

  // Stale-entity check: if a staff user has an acting-as id pointing to a deleted
  // (or no-longer-accessible) entity, silently clear it. Otherwise every API call
  // would silently break with "No organization context available".
  useEffect(() => {
    if (currentUserData.user?.user_type !== UserTypeEnum.STAFF || !actingAsEntity) return;
    let cancelled = false;
    HealthcareEntitiesService.healthcareEntitiesRetrieve({ id: actingAsEntity.id }).catch(
      (err: ApiError) => {
        if (cancelled) return;
        if (err?.status === 404) {
          writeActingAsEntity(null);
          _setActingAsEntity(null);
        }
        // Network/5xx are transient — leave the selection alone.
      }
    );
    return () => {
      cancelled = true;
    };
    // Intentionally only checks on user-type change/mount, not on every entity change
    // (which would hammer the endpoint as the user clicks through the picker).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserData.user?.user_type]);

  // Handle session establishment (e.g., after successful login)
  const handleSessionEstablished = () => {
    setSessionExists(true);
    setInitialCheckComplete(false);
    // Only refetch if we don't already have user data
    if (!currentUserData?.user) {
      currentUserData.refetch();
    }
  };

  const shouldShowLoading =
    !initialCheckComplete ||
    ((sessionExists || (isOptionalAuthRoute && hasSessionIndicator)) &&
      currentUserData.isLoading &&
      !shouldSkipSessionCheck);

  // Show loading spinner while checking session or loading user data
  if (shouldShowLoading) {
    return (
      // techdebt: change to tailwind
      <Container
        className="content-wrapper justify-content-center align-items-center text-center"
        fluid
      >
        <Spinner
          animation="grow"
          aria-label="Loading"
          className="loading-spinner tw:mt-8"
          variant="primary"
        />
      </Container>
    );
  }

  return (
    <SessionContext.Provider
      value={{
        ...currentUserData,
        sessionExists,
        setSessionExists: handleSessionEstablished,
        notifications: currentUserNotifications,
        logout: () => mutationLogout.mutateAsync(),
        currentEntity: currentUserData.user?.healthcare_entity_name,
        actingAsEntity,
        setActingAsEntity,
        audience:
          currentUserData.user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP
            ? 'healthcare_entity'
            : currentUserData.user?.user_type,
        audienceIsProvider: currentUserData.user?.user_type === UserTypeEnum.PROVIDER,
        audienceIsOrg: currentUserData.user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP,
        audienceIsStaff: currentUserData.user?.user_type === UserTypeEnum.STAFF,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}
